import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Utility class for detecting and resolving duplicate detentions
 */
export class DuplicateDetentionDetector {
  constructor() {
    this.queueCollection = 'disciplineQueue';
    this.resolutionQueueCollection = 'duplicateResolutionQueue';
  }

  /**
   * Check for duplicate detentions before creating a new one
   * @param {Object} detentionData - Data for the detention being created
   * @returns {Object} Detection result with duplicates found
   */
  async checkForDuplicateDetentions(detentionData) {
    try {
      // Only check for detention-type consequences
      if (detentionData.type !== 'detention' && detentionData.consequenceType !== 'detention') {
        return { hasDuplicates: false, duplicates: [] };
      }

      const { studentId, offence, reason, issuedAt, schoolId } = detentionData;

      // CRITICAL: Never query without schoolId
      if (!schoolId) {
        console.warn('DuplicateDetentionDetector: schoolId required but not provided');
        return { hasDuplicates: false, duplicates: [] };
      }

      if (!studentId || (!offence && !reason)) {
        return { hasDuplicates: false, duplicates: [] };
      }

      // Get the current time or use provided issuedAt
      const currentTime = issuedAt?.toDate?.() || issuedAt || new Date();
      const sixtySecondsAgo = new Date(currentTime.getTime() - 60000);

      // Query for recent detentions for the same student
      const q = query(
        collection(db, this.queueCollection),
        where('schoolId', '==', schoolId),
        where('studentId', '==', studentId),
        where('type', '==', 'detention'),
        where('undone', '==', false),
        where('issuedAt', '>=', Timestamp.fromDate(sixtySecondsAgo))
      );

      const querySnapshot = await getDocs(q);
      const duplicates = [];

      querySnapshot.forEach(doc => {
        const existingDetention = doc.data();

        // Check if it's the same offense (match by offense or reason)
        const existingOffense = existingDetention.offence || existingDetention.reason || existingDetention.offenceName;
        const newOffense = offence || reason;

        if (existingOffense === newOffense) {
          // Check if it's the same date (within the same day)
          const existingDate = existingDetention.issuedAt?.toDate?.() || new Date(existingDetention.issuedAt);
          const isSameDay = this.isSameDay(existingDate, currentTime);

          if (isSameDay) {
            duplicates.push({
              id: doc.id,
              ...existingDetention,
              matchedOn: {
                student: true,
                offense: true,
                date: true,
                timeProximity: true
              }
            });
          }
        }
      });

      return {
        hasDuplicates: duplicates.length > 0,
        duplicates: duplicates,
        duplicateCount: duplicates.length
      };
    } catch (error) {
      console.error('Error checking for duplicate detentions:', error);
      // Don't fail detention creation due to duplicate check errors
      return { hasDuplicates: false, duplicates: [], error: error.message };
    }
  }

  /**
   * Create a resolution queue entry for detected duplicates
   * @param {Object} newDetention - The new detention data that would be duplicated
   * @param {Array} duplicates - Array of duplicate detentions found
   * @returns {Object} Created resolution queue entry
   */
  async createResolutionQueueEntry(newDetention, duplicates) {
    try {
      const resolutionEntry = {
        status: 'pending_review',
        type: 'duplicate_detention',
        studentId: newDetention.studentId,
        studentName: newDetention.studentName,
        schoolId: newDetention.schoolId, // Multi-school support
        offense: newDetention.offence || newDetention.reason,

        // Store the new detention data (not created yet)
        proposedDetention: {
          ...newDetention,
          blocked: true,
          blockedReason: 'Duplicate detected'
        },

        // Store all duplicate detention IDs and summaries
        duplicateDetentions: duplicates.map(d => ({
          id: d.id,
          ruleName: d.ruleName,
          triggeredByRule: d.triggeredByRule,
          issuedAt: d.issuedAt,
          createdBy: d.createdBy
        })),

        // Metadata
        detectedAt: Timestamp.now(),
        duplicateCount: duplicates.length + 1, // Include the proposed one

        // Resolution options
        resolutionOptions: [
          {
            action: 'keep_first',
            label: 'Keep First Only',
            description: 'Keep the earliest detention and discard all others'
          },
          {
            action: 'merge',
            label: 'Merge Detentions',
            description: 'Create a single detention with combined notes from all rules'
          },
          {
            action: 'keep_all',
            label: 'Keep All',
            description: 'Mark all detentions as intentional and keep them separate'
          }
        ],

        // Tracking
        resolved: false,
        resolvedBy: null,
        resolvedAt: null,
        resolutionAction: null,
        resolutionNotes: null
      };

      const docRef = await addDoc(collection(db, this.resolutionQueueCollection), resolutionEntry);

      console.log('Created resolution queue entry:', docRef.id);
      return { id: docRef.id, ...resolutionEntry };
    } catch (error) {
      console.error('Error creating resolution queue entry:', error);
      throw error;
    }
  }

  /**
   * Resolve duplicate detentions based on selected action
   * @param {string} resolutionId - ID of the resolution queue entry
   * @param {string} action - Resolution action (keep_first, merge, keep_all)
   * @param {string} resolvedBy - User ID who resolved it
   * @param {string} notes - Optional resolution notes
   */
  async resolveDuplicates(resolutionId, action, resolvedBy, notes = '') {
    try {
      // Get the resolution entry
      const resolutionDoc = await getDocs(
        query(
          collection(db, this.resolutionQueueCollection),
          where('__name__', '==', resolutionId)
        )
      );

      if (resolutionDoc.empty) {
        throw new Error('Resolution entry not found');
      }

      const resolution = resolutionDoc.docs[0].data();
      const duplicateIds = resolution.duplicateDetentions.map(d => d.id);

      let resultDetentionId = null;

      switch (action) {
        case 'keep_first': {
          // Sort duplicates by issuedAt to find the earliest
          const sorted = [...resolution.duplicateDetentions].sort((a, b) => {
            const timeA = a.issuedAt?.seconds || 0;
            const timeB = b.issuedAt?.seconds || 0;
            return timeA - timeB;
          });

          const keepId = sorted[0].id;
          resultDetentionId = keepId;

          // Mark all others as undone
          for (const dupId of duplicateIds) {
            if (dupId !== keepId) {
              await updateDoc(doc(db, this.queueCollection, dupId), {
                undone: true,
                undoneBy: 'duplicate-resolution',
                undoneAt: Timestamp.now(),
                undoneReason: `Duplicate resolved: kept earliest detention ${keepId}`,
                resolutionId: resolutionId
              });
            }
          }
          break;
        }

        case 'merge': {
          // Create a merged detention with combined information
          const mergedNotes = resolution.duplicateDetentions
            .map(d => `Rule: ${d.ruleName || 'Unknown'}`)
            .join('; ');

          const mergedDetention = {
            ...resolution.proposedDetention,
            notes: `Merged detention from ${duplicateIds.length} duplicate entries. ${mergedNotes}`,
            mergedFrom: duplicateIds,
            createdBy: 'duplicate-resolution',
            createdAt: Timestamp.now(),
            issuedAt: Timestamp.now()
          };

          // Remove the blocked flag since we're creating it intentionally
          delete mergedDetention.blocked;
          delete mergedDetention.blockedReason;

          // Create the merged detention
          const docRef = await addDoc(collection(db, this.queueCollection), mergedDetention);
          resultDetentionId = docRef.id;

          // Mark all original duplicates as undone
          for (const dupId of duplicateIds) {
            await updateDoc(doc(db, this.queueCollection, dupId), {
              undone: true,
              undoneBy: 'duplicate-resolution',
              undoneAt: Timestamp.now(),
              undoneReason: `Merged into detention ${docRef.id}`,
              mergedInto: docRef.id,
              resolutionId: resolutionId
            });
          }
          break;
        }

        case 'keep_all': {
          // Mark all detentions as reviewed and intentional
          for (const dupId of duplicateIds) {
            await updateDoc(doc(db, this.queueCollection, dupId), {
              duplicateReviewed: true,
              duplicateIntentional: true,
              reviewedBy: resolvedBy,
              reviewedAt: Timestamp.now(),
              resolutionId: resolutionId
            });
          }

          // Also create the proposed detention since it was blocked
          const proposedDetention = { ...resolution.proposedDetention };
          delete proposedDetention.blocked;
          delete proposedDetention.blockedReason;
          proposedDetention.duplicateReviewed = true;
          proposedDetention.duplicateIntentional = true;

          const docRef = await addDoc(collection(db, this.queueCollection), proposedDetention);
          resultDetentionId = docRef.id;
          break;
        }

        default:
          throw new Error(`Unknown resolution action: ${action}`);
      }

      // Update the resolution entry as resolved
      await updateDoc(doc(db, this.resolutionQueueCollection, resolutionId), {
        resolved: true,
        resolvedBy: resolvedBy,
        resolvedAt: Timestamp.now(),
        resolutionAction: action,
        resolutionNotes: notes,
        resultDetentionId: resultDetentionId,
        status: 'resolved'
      });

      // Create audit log entry
      await this.createAuditLog({
        action: 'duplicate_resolution',
        resolutionId: resolutionId,
        resolutionAction: action,
        affectedDetentions: duplicateIds,
        resultDetentionId: resultDetentionId,
        resolvedBy: resolvedBy,
        notes: notes
      });

      return {
        success: true,
        resolutionId: resolutionId,
        action: action,
        resultDetentionId: resultDetentionId
      };
    } catch (error) {
      console.error('Error resolving duplicates:', error);
      throw error;
    }
  }

  /**
   * Create audit log entry for resolution actions
   */
  async createAuditLog(logData) {
    try {
      await addDoc(collection(db, 'disciplineAuditLog'), {
        ...logData,
        timestamp: Timestamp.now()
      });
    } catch (error) {
      console.error('Error creating audit log:', error);
      // Don't throw - audit logging should not break the main flow
    }
  }

  /**
   * Get pending resolution queue items
   */
  async getPendingResolutions(schoolId) {
    try {
      // Guard: require schoolId to prevent permission errors
      if (!schoolId) {
        console.warn('DuplicateDetentionDetector: schoolId required but not provided');
        return [];
      }

      const q = query(
        collection(db, this.resolutionQueueCollection),
        where('schoolId', '==', schoolId),
        where('resolved', '==', false),
        where('status', '==', 'pending_review')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      // Collection might not exist yet - this is normal
      if (error.code === 'failed-precondition' || error.message?.includes('requires an index')) {
        console.log('Resolution queue collection does not exist yet or needs index - this is normal');
        return [];
      }
      console.error('Error fetching pending resolutions:', error);
      return [];
    }
  }

  /**
   * Helper to check if two dates are the same day
   */
  isSameDay(date1, date2) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  }
}

// Export singleton instance
export const duplicateDetentionDetector = new DuplicateDetentionDetector();